import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, User, Calendar, Brain, Sparkles, FileText } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserPartner } from '../../lib/auth';
import { getPartnerIdForUser } from '../../lib/teams';
import { ResultsGraph } from '../../components/ResultsGraph';
import { HarmonicGraph } from '../../components/HarmonicGraph';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import type { AssessmentResult } from '../../types/results';

export function TeamMemberResults() {
  const navigate = useNavigate();
  const { teamId, userId } = useParams<{ teamId: string; userId: string }>();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [results, setResults] = useState<AssessmentResult[]>([]);
  const [selectedVersion, setSelectedVersion] = useState(0);
  const [memberName, setMemberName] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user || !teamId || !userId) {
      navigate('/login');
      return;
    }

    const init = async () => {
      try {
        const isPartner = await isUserPartner(user.id);
        if (!isPartner) {
          navigate('/login');
          return;
        }

        const pId = await getPartnerIdForUser(user.id);
        if (!pId) {
          navigate('/login');
          return;
        }

        const { data: team } = await supabase
          .from('teams')
          .select('partner_id')
          .eq('id', teamId)
          .maybeSingle();

        if (!team || team.partner_id !== pId) {
          navigate('/partner/teams');
          return;
        }

        const { data: membership } = await supabase
          .from('team_members')
          .select('id')
          .eq('team_id', teamId)
          .eq('user_id', userId)
          .maybeSingle();

        if (!membership) {
          navigate(`/partner/teams/${teamId}`);
          return;
        }

        const { data: userData } = await supabase
          .from('users')
          .select('full_name, email')
          .eq('id', userId)
          .maybeSingle();

        setMemberName(userData?.full_name || userData?.email || 'Member');

        const { data: resultsData } = await supabase
          .from('results')
          .select('*')
          .eq('user_id', userId)
          .order('completed_at', { ascending: false });

        const mapped = (resultsData || []).map(r => ({
          id: r.id,
          userId: r.user_id,
          coachId: r.coach_id || undefined,
          partnerId: r.partner_id || undefined,
          scores: r.scores as any,
          harmonicScores: r.harmonic_scores as any,
          completedAt: new Date(r.completed_at || Date.now()),
          aiAnalysis: r.ai_analysis || undefined,
          version: r.version || 1,
          originalResultId: r.original_result_id || undefined,
        })) as AssessmentResult[];

        setResults(mapped);
      } catch (err) {
        console.error('Error loading member results:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate, teamId, userId]);

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Loading results..." />;
  }

  if (results.length === 0) {
    return (
      <div className="min-h-screen freud-bg py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="freud-card-elevated p-8">
            <FileText className="w-16 h-16 text-cream-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-chocolate-900 mb-2">No Results Found</h2>
            <p className="text-chocolate-500 mb-6">
              This member hasn't completed an assessment yet.
            </p>
            <Link
              to={`/partner/teams/${teamId}`}
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Team
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selectedResult = results[selectedVersion];

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
          <Link
            to={`/partner/teams/${teamId}`}
            className="flex items-center gap-2 text-chocolate-500 hover:text-sage-600 mb-4 transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Team
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-sage-100 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-sage-600" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-chocolate-900">{memberName}</h1>
                <div className="flex items-center gap-2 text-sm text-chocolate-500">
                  <Calendar className="w-4 h-4" />
                  Completed: {selectedResult.completedAt.toLocaleDateString()}
                </div>
              </div>
            </div>

            {results.length > 1 && (
              <select
                value={selectedVersion}
                onChange={(e) => setSelectedVersion(Number(e.target.value))}
                className="freud-input w-auto"
              >
                {results.map((_, index) => (
                  <option key={index} value={index}>
                    Version {results.length - index}
                    {index === 0 ? ' (Latest)' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8 space-y-8">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="freud-card-elevated p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
                <Brain className="w-5 h-5 text-sage-600" />
              </div>
              <h2 className="text-xl font-bold text-chocolate-900">
                Characteristic Profile
              </h2>
            </div>
            <ResultsGraph scores={selectedResult.scores} />
          </div>

          <div className="freud-card-elevated p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-coral-100 rounded-xl flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-coral-600" />
              </div>
              <h2 className="text-xl font-bold text-chocolate-900">
                Harmonic Scale Profile
              </h2>
            </div>
            <HarmonicGraph scores={selectedResult.harmonicScores} />
          </div>
        </div>
      </main>
    </div>
  );
}
